import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Auth from "@/Auth/Auth";
import UserIdentitiesService, { UserIdentityType } from "@/services/UserIdentitiesService";
import savingsPlans, { SavingsPlanType } from "@/services/SavingsPlanService";
import savingsPlansUserIdentitiesService from "@/services/SavingsPlanUserIdentityService";
import SuitabilityTestsService, { ISuitabilityTest, ISuitabilityTestVersion } from "@/services/SuitabilityTestsService";
import FinametrixService, { RiskType } from "@/services/FinametrixService";
import { mapAxiosErrorToOnboardingEnvelope, type CompatibleAlternative, type PlanRiskProfileEvaluation } from "@/services/SavingsPlanOnboarding.types";
import { ContractOnboardingStrings, resolveContractOnboardingError } from "@/screen/Map/contractOnboarding.strings";
import { buildMapNavigationState } from "@/screen/Map/resolveContractOnboardingStepNavigation";
import { isMinorBirthday } from "@/screen/ContractManagement/helpers/intervenerSelectionHelpers";
import { getInForceSuitabilityTests, getLatestInForceSignedTestWithProfile, getSuitabilityNextUpdateAt, hasOnlyExpiredSignedSuitabilityTests } from "@/screen/ContractManagement/helpers/suitabilityTestValidity";
import type { SuitabilityRow, SuitabilityStepControllerReturn } from "@/screen/ContractManagement/Interfaces/SuitabilityStepInterfaces";

// Espejo de PlanRiskProfileRules.GetLatestSignedSuitabilityProfileId del backend.
// Toma el test SIGNED y vigente (no caducado, ver suitabilityTestValidity) más reciente por fecha
// de firma que tenga perfil, nunca un DRAFT ni el último de la lista, para no mostrar un perfil de
// riesgo distinto del que el backend usa para decidir la compatibilidad de la cartera.
// Si hay test firmado vigente pero sin perfil válido (ausente o fuera del catálogo de Finametrix) el
// backend asigna order 0 (incompatible); lo reflejamos como "0/4" en vez de dejar la celda vacía.
function resolveLatestSignedRiskLabel(tests: ISuitabilityTest[], risks: RiskType[] | undefined, now: Date): string
{
    const inForceTests = getInForceSuitabilityTests(tests, now);
    if(inForceTests.length === 0)
    {
        return "";
    }

    const latestWithProfile = getLatestInForceSignedTestWithProfile(inForceTests, now);
    if(!latestWithProfile)
    {
        return "0/4";
    }

    const profileId = latestWithProfile.results?.results?.profile;
    const risk = (risks || []).find((r) => r._id === profileId);
    if(!risk)
    {
        return "0/4";
    }

    return `${risk.order || 0}/4 ${risk.name || ""}`.trim();
}

// advanceableStepKey llega a null cuando se entra al paso ya superado (reentrada para rehacer el
// test): la pantalla sigue operativa, pero sin avanzar el onboarding.
export function useSuitabilityStepController(advanceableStepKey: string | null): SuitabilityStepControllerReturn
{
    const { savingsPlanId: savingsPlanIdParam } = useParams<{ savingsPlanId: string }>();
    const savingsPlanId = Number(savingsPlanIdParam);
    const history = useHistory();
    const user = Auth.getUserProfile();

    const [savingsPlan, setSavingsPlan] = useState<SavingsPlanType>();
    const [rows, setRows] = useState<SuitabilityRow[]>([]);
    const [risks, setRisks] = useState<RiskType[]>();
    const [suitabilityTestVersion, setSuitabilityTestVersion] = useState<ISuitabilityTestVersion>();
    const [riskEvaluation, setRiskEvaluation] = useState<PlanRiskProfileEvaluation | null>(null);
    const [riskEvaluationFailed, setRiskEvaluationFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isChangingPortfolio, setIsChangingPortfolio] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [infoMessage, setInfoMessage] = useState<string | null>(null);

    const refreshRiskEvaluation = useCallback(async () =>
    {
        const envelope = await savingsPlans.getRiskProfileEvaluation(savingsPlanId);
        if(!envelope.success || !envelope.data)
        {
            // Distinguimos "evaluación fallida" de "todavía no evaluado": marcamos el fallo
            // para ofrecer reintento y permitir el avance (manda el backend, que aplica la
            // reja dura de riesgo y devolvería RISK_PROFILE_NOT_COMPATIBLE si procede).
            setErrorMessage(resolveContractOnboardingError(envelope.errors?.[0], ContractOnboardingStrings.suitabilityRiskEvaluationFailed));
            setRiskEvaluation(null);
            setRiskEvaluationFailed(true);
            return null;
        }

        setRiskEvaluation(envelope.data);
        setRiskEvaluationFailed(false);
        return envelope.data;
    }, [savingsPlanId]);

    const load = useCallback(async () =>
    {
        if(!user?.id || Number.isNaN(savingsPlanId))
        {
            setIsLoading(false);
            setErrorMessage("No se ha podido cargar la idoneidad del plan.");
            return;
        }

        setIsLoading(true);
        setErrorMessage(null);

        try
        {
            const [plan, spuiList, identities, version, risksAux] = await Promise.all(
            [
                savingsPlans.getSavingsPlan(savingsPlanId),
                savingsPlansUserIdentitiesService.getSavingsPlanUserIdentitiesBySavingsPlanId(savingsPlanId),
                UserIdentitiesService.getIdentitiesByUserId(user.id),
                SuitabilityTestsService.getLastSuitabilityTestVersion(),
                FinametrixService.getRisks(),
            ]);

            setSavingsPlan(plan);
            setSuitabilityTestVersion(version);
            setRisks(risksAux);

            // Replicamos GetSuitabilityRequiredLinks del backend: una fila por cada SPUI con rol
            // (relation !== 0). NO partimos de getIdentitiesByUserId: un interviniente del plan que
            // ya no aparece ahí (p. ej. préstamo revocado) seguiría exigiendo test en backend y, si
            // no generásemos fila, allRequiredTestsComplete podría ser true → STEP_NOT_COMPLETE.
            const requiredSpui = (spuiList || []).filter((s) => s.relation !== 0);

            // El backend considera el paso completo únicamente cuando el interviniente tiene un
            // SuitabilityTest en estado SIGNED. Los tests se leen desde el plan (fuente fiable).
            const planIdentityById = new Map<number, UserIdentityType>();
            const testsByIdentityId = new Map<number, ISuitabilityTest[]>();
            ((plan?.savingsPlansUserIdentities as any[]) || []).forEach((spui) =>
            {
                const identityId: number | undefined = spui?.userIdentity?.id ?? spui?.userIdentityId;
                if(identityId == null)
                {
                    return;
                }
                if(spui?.userIdentity)
                {
                    planIdentityById.set(identityId, spui.userIdentity as UserIdentityType);
                }
                const identityTests = spui?.userIdentity?.suitabilityTests;
                if(Array.isArray(identityTests))
                {
                    testsByIdentityId.set(identityId, identityTests);
                }
            });

            // getByUserId aporta flags de UI (canActivateSuitabilityTestButton, status); no es la lista de filas.
            const identitiesById = new Map<number, UserIdentityType>();
            (identities || []).forEach((identity) =>
            {
                if(identity.id != null)
                {
                    identitiesById.set(identity.id, identity);
                }
            });

            // Misma fecha de referencia para todas las filas de esta carga.
            const now = new Date();
            const mappedRows: SuitabilityRow[] = requiredSpui.map((spui) =>
            {
                const identityId = spui.userIdentityId;
                const fromPlan = planIdentityById.get(identityId) ?? spui.userIdentity;
                const fromUserList = identitiesById.get(identityId);
                // Preferimos datos del plan para nombre/birthday/tests; overlay de getByUserId para flags de UI.
                const identity: UserIdentityType =
                {
                    ...(fromPlan || {}),
                    ...(fromUserList || {}),
                    id: identityId,
                };

                const isMinor = isMinorBirthday(identity.birthday);
                const tests = testsByIdentityId.get(identityId) ?? identity.suitabilityTests ?? [];
                // El avance de 2.2.2 exige firma, menos de 2 años y la versión actual del cuestionario.
                const currentVersionId = version?.id;
                const hasSignedTest = getInForceSuitabilityTests(tests, now, currentVersionId).length > 0;
                const hasCompletedTest = !isMinor && hasSignedTest;
                const hasOnlyExpiredTests = !isMinor && hasOnlyExpiredSignedSuitabilityTests(tests, now, currentVersionId);
                const riskLabel = resolveLatestSignedRiskLabel(tests, risksAux, now);
                const suitabilityNextUpdateAt = identity.canActivateSuitabilityTestButton === false
                    ? getSuitabilityNextUpdateAt(tests, now, currentVersionId)
                    : null;
                return { identity, isMinor, hasCompletedTest, hasOnlyExpiredTests, riskLabel, suitabilityNextUpdateAt };
            });
            setRows(mappedRows);

            const allComplete = mappedRows.every((row) => row.isMinor || row.hasCompletedTest);
            if(allComplete)
            {
                await refreshRiskEvaluation();
            }
            else
            {
                // Todavía no procede evaluar (faltan tests): no es un fallo.
                setRiskEvaluation(null);
                setRiskEvaluationFailed(false);
            }
        }
        catch(error: any)
        {
            // Traducimos el error de axios a mensaje de negocio (403/404/red) en lugar
            // de propagar el texto técnico de axios.
            const envelope = mapAxiosErrorToOnboardingEnvelope(error);
            setErrorMessage(resolveContractOnboardingError(envelope.errors?.[0], "No se ha podido cargar la idoneidad del plan."));
        }
        finally
        {
            setIsLoading(false);
        }
    }, [refreshRiskEvaluation, savingsPlanId, user?.id]);

    useEffect(() =>
    {
        void load();
    }, [load]);

    const allRequiredTestsComplete = useMemo(() => rows.length > 0 && rows.every((row) => row.isMinor || row.hasCompletedTest), [rows]);

    const canAdvance = useMemo(() =>
    {
        if(advanceableStepKey === null || !allRequiredTestsComplete || isSubmitting || isChangingPortfolio || isLoading)
        {
            return false;
        }

        if(!riskEvaluation)
        {
            // Sin evaluación de UX: si el endpoint falló, dejamos avanzar y que el backend
            // aplique la reja dura de riesgo. Si simplemente aún no se ha evaluado, no.
            return riskEvaluationFailed;
        }

        if(!riskEvaluation.isApplicable)
        {
            return true;
        }

        return riskEvaluation.isCompatible;
    }, [advanceableStepKey, allRequiredTestsComplete, isChangingPortfolio, isLoading, isSubmitting, riskEvaluation, riskEvaluationFailed]);

    const onStartTest = useCallback((userIdentityId: number) =>
    {
        history.push(`/contract/${savingsPlanId}/suitabilityTest/test/${userIdentityId}`);
    }, [history, savingsPlanId]);

    const onSelectAlternative = useCallback(async (alternative: CompatibleAlternative) =>
    {
        setIsChangingPortfolio(true);
        setErrorMessage(null);
        setInfoMessage(null);

        try
        {
            const updated = await savingsPlans.patchSavingsPlan(savingsPlanId,
            {
                managedPortfolioId: alternative.id,
            });
            setSavingsPlan(updated);

            const evaluation = await refreshRiskEvaluation();
            if(evaluation?.isCompatible)
            {
                setInfoMessage(ContractOnboardingStrings.suitabilityAlternativeChanged);
            }
        }
        catch(error: any)
        {
            setErrorMessage(error?.message || "No se ha podido cambiar la cartera del plan.");
        }
        finally
        {
            setIsChangingPortfolio(false);
        }
    }, [refreshRiskEvaluation, savingsPlanId]);

    const onAdvance = useCallback(async () =>
    {
        if(!canAdvance)
        {
            if(!allRequiredTestsComplete)
            {
                setErrorMessage(ContractOnboardingStrings.suitabilityTestsIncomplete);
            }
            return;
        }

        setIsSubmitting(true);
        setErrorMessage(null);

        try
        {
            // Guarda de sincronía: se declara el paso que la guarda resolvió como vivo (2.2.2 en el
            // tronco, 2.4.3 en la rama de mayoría de edad).
            if(advanceableStepKey === null)
            {
                setIsSubmitting(false);
                return;
            }

            const result = await savingsPlans.advancePlanStep(savingsPlanId, advanceableStepKey);
            if(!result.success)
            {
                const firstError = result.errors?.[0];
                setErrorMessage(resolveContractOnboardingError(firstError, ContractOnboardingStrings.suitabilityAdvanceFailed));

                if(firstError?.code === "RISK_PROFILE_NOT_COMPATIBLE")
                {
                    await refreshRiskEvaluation();
                }

                setIsSubmitting(false);
                return;
            }

            history.push("/configurationMap", buildMapNavigationState(savingsPlanId, savingsPlan?.name));
        }
        catch(error: any)
        {
            setErrorMessage(error?.message || ContractOnboardingStrings.suitabilityAdvanceFailed);
            setIsSubmitting(false);
        }
    }, [advanceableStepKey, allRequiredTestsComplete, canAdvance, history, refreshRiskEvaluation, savingsPlan?.name, savingsPlanId]);

    const onRetryRiskEvaluation = useCallback(async () =>
    {
        setErrorMessage(null);
        await refreshRiskEvaluation();
    }, [refreshRiskEvaluation]);

    const onBackToMap = useCallback(() =>
    {
        history.push("/configurationMap", buildMapNavigationState(savingsPlanId, savingsPlan?.name));
    }, [history, savingsPlan?.name, savingsPlanId]);

    return{
        savingsPlanId,
        savingsPlan,
        rows,
        risks,
        suitabilityTestVersion,
        riskEvaluation,
        isLoading,
        isSubmitting,
        isChangingPortfolio,
        errorMessage,
        infoMessage,
        allRequiredTestsComplete,
        canAdvance,
        riskEvaluationFailed,
        onStartTest,
        onSelectAlternative,
        onAdvance,
        onRetryRiskEvaluation,
        onBackToMap,
    };
}